/*!
 * GentlePup theme — cart-utilities.js
 * Copyright © 2026 Murilo Scigliano. All rights reserved.
 * Proprietary. See LICENSE. dev@murilo.design
 */
/**
 * The cart utility sheets.
 *
 * Tapping a pill raises a sheet from the foot of the cart panel. The
 * alternative — expanding in place — grew the footer by 83px and its
 * scroll height by 214px, so the subtotal and the checkout button moved
 * at the moment the buyer was reaching for them.
 *
 * The sheet overlays the footer rather than joining the flow, so nothing
 * behind it shifts. Only one is ever open.
 *
 * Everything renders and works before this file runs: without it the
 * sheets are ordinary sections and the fields still submit. The class on
 * the host is what switches them to overlays, so a failed load leaves a
 * plain, usable cart rather than three invisible panels.
 */

class CartUtilities extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    // Only now that the script is running do the sheets become overlays.
    this.classList.add("cart-utilities--enhanced");

    // The sheet is anchored to the top of the footer, whose height
    // changes with the cart, so it is measured rather than guessed.
    this.measureFooter();
    this.resizeObserver = new ResizeObserver(() => this.measureFooter());
    const footer = this.closest(".cart-drawer__footer");
    if (footer) this.resizeObserver.observe(footer);

    this.addEventListener("click", this.handleClick, { signal });
    this.addEventListener("keydown", this.handleKeydown, { signal });

    // A click anywhere else closes the sheet, the way a popover behaves.
    document.addEventListener("click", this.handleOutsideClick, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
  }

  /** Publishes the footer height for the sheet to sit above. */
  measureFooter() {
    const footer = this.closest(".cart-drawer__footer");
    const panel = this.closest(".cart-drawer__panel");
    if (!footer || !panel) return;

    panel.style.setProperty(
      "--cart-footer-height",
      `${Math.round(footer.getBoundingClientRect().height)}px`
    );
  }

  get openName() {
    return this.getAttribute("data-open") || null;
  }

  handleClick = (event) => {
    const opener = event.target.closest("[data-utility-open]");
    if (opener) {
      const name = opener.dataset.utilityOpen;
      // Tapping the open pill again closes it — the pill is a toggle, not
      // a one-way door.
      if (this.openName === name) this.close();
      else this.open(name);
      return;
    }

    if (event.target.closest("[data-utility-close]")) this.close();
  };

  handleKeydown = (event) => {
    if (event.key !== "Escape" || !this.openName) return;

    // preventDefault, not just stopPropagation. Escape inside a <dialog>
    // is handled by the browser as a request to close the dialog, so
    // without this the first Escape dismissed the whole cart — the
    // buyer lost the drawer when they meant to dismiss a sheet.
    event.preventDefault();
    event.stopPropagation();
    this.close({ restoreFocus: true });
  };

  handleOutsideClick = (event) => {
    if (!this.openName) return;
    if (this.contains(event.target)) return;
    this.close();
  };

  open(name) {
    const sheet = this.querySelector(`[data-utility-sheet="${name}"]`);
    if (!sheet) return;

    this.setAttribute("data-open", name);
    this.syncPills();

    // The field, not the close button. Querying for "textarea, input,
    // button" matched in DOM order, and the close button comes first —
    // so opening the sheet put the cursor on Close rather than in the
    // field the buyer just asked for. A sheet with no field (delivery)
    // falls back to its close button, which is the only thing to focus.
    const field = sheet.querySelector("textarea, input");
    (field || sheet.querySelector("[data-utility-close]"))?.focus({ preventScroll: true });
  }

  close({ restoreFocus = false } = {}) {
    const name = this.openName;
    if (!name) return;

    this.removeAttribute("data-open");
    this.syncPills();

    if (restoreFocus) {
      this.querySelector(`[data-utility-open="${name}"]`)?.focus({ preventScroll: true });
    }
  }

  /** aria-expanded has to follow the visual state, not lead it. */
  syncPills() {
    const open = this.openName;
    this.querySelectorAll("[data-utility-open]").forEach((pill) => {
      pill.setAttribute("aria-expanded", String(pill.dataset.utilityOpen === open));
    });
  }
}

if (!customElements.get("gp-cart-utilities")) {
  customElements.define("gp-cart-utilities", CartUtilities);
}
